import gsap from 'gsap';
import { CustomEase } from 'gsap/dist/CustomEase';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useEffect } from 'react';

export interface PushFadeOptions {
  delay?: number;
  disabled?: boolean;
  duration?: number;
  ease?: gsap.EaseString | gsap.EaseFunction;
  interval?: number;
  animateContainer?: boolean;
}

export const usePushFade = (
  container: HTMLElement | null,
  options?: PushFadeOptions,
) => {
  useEffect(() => {
    if (options?.disabled) return;

    gsap.registerPlugin(ScrollTrigger, CustomEase);

    CustomEase.create('cointrackerSmooth', '.2,.22,.11,1.0');
    CustomEase.create('cointrackerBounce', '.17,.88,.32,1.8');

    if (!container) return;

    const items = container.querySelectorAll<HTMLElement>(
      '[data-push-fade-item]',
    );

    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top bottom',
        toggleActions: 'play none none none',
      },
      delay: options?.delay ?? 0.3,
      defaults: {
        duration: options?.duration ?? 0.4,
      },
    });

    items.forEach((section, index) => {
      timeline.fromTo(
        section,
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
        },
        `+=${options?.interval ?? 0.35}`,
      );

      timeline.fromTo(
        options?.animateContainer ? container : items,
        {
          y: () => getOffset(Array.from(items).slice(index)),
        },
        {
          y: () => getOffset(Array.from(items).slice(index + 1)),
          ease: options?.ease ?? 'cointrackerSmooth',
        },
        '<',
      );
    });

    return () => {
      timeline.kill();
    };
  }, [container, options]);
};

function getOffset(elements: HTMLElement[]): number {
  if (!elements.length) return 0;

  const res =
    elements
      .map((item) => item.getBoundingClientRect().height)
      .reduce((a, b) => a + b) * 0.5;

  return res;
}
